
import React, { createContext, useContext, useState, useCallback, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useGlobalSearch as useGlobalSearchHook, GlobalSearchResult } from '@/hooks/useGlobalSearch';

export interface SearchResult {
  id: string;
  type: 'prayer' | 'verse' | 'chapter';
  title: string;
  content: string;
  reference?: string;
  author?: string;
  category?: string;
  isChapter?: boolean;
}

interface SearchContextType {
  open: boolean;
  query: string;
  results: SearchResult[];
  isSearching: boolean;
  globalResults: GlobalSearchResult;
  setOpen: (open: boolean) => void;
  setQuery: (q: string) => void;
}

const SearchContext = createContext<SearchContextType | null>(null);

export function SearchProvider({ children }: { children: React.ReactNode }) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<SearchResult[]>([]);
  const [isSearching, setIsSearching] = useState(false);

  // Use the enhanced global search hook
  const globalResults = useGlobalSearchHook(query);

  // Debounced search function
  const performSearch = useCallback(async (searchQuery: string) => {
    if (!searchQuery.trim() || searchQuery.trim().length < 2) {
      setResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const searchResults: SearchResult[] = [];

      // Search prayers
      const { data: prayers } = await supabase
        .from('prayers')
        .select('id, title, content, category, author_name')
        .or(`title.ilike.%${searchQuery}%,content.ilike.%${searchQuery}%`)
        .eq('visibility', 'public')
        .limit(5);

      if (prayers) {
        searchResults.push(...prayers.map(prayer => ({
          id: prayer.id,
          type: 'prayer' as const,
          title: prayer.title || 'Untitled Prayer',
          content: prayer.content,
          category: prayer.category,
          author: prayer.author_name
        })));
      }

      // Add results from global search
      if (globalResults.verses) {
        searchResults.push(...globalResults.verses.slice(0, 3).map((verse: any) => ({
          id: `verse-${verse.reference}`,
          type: 'verse' as const,
          title: verse.reference,
          content: verse.text || verse.content,
          reference: verse.reference,
          isChapter: false
        })));
      }

      // Add trending verses as suggestions
      if (globalResults.trending) {
        searchResults.push(...globalResults.trending.slice(0, 2).map((trending: any) => ({
          id: `trending-${trending.reference}`,
          type: 'verse' as const,
          title: `📈 ${trending.reference}`,
          content: trending.content || 'Trending verse',
          reference: trending.reference,
          isChapter: false
        })));
      }

      setResults(searchResults.slice(0, 10)); // Limit total results
    } catch (error) {
      console.error('Search failed:', error);
      setResults([]);
    } finally {
      setIsSearching(false);
    }
  }, [globalResults]);

  // Debounce search when query changes
  useEffect(() => {
    if (!open) return;
    
    const timeoutId = setTimeout(() => {
      performSearch(query);
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [query, open, performSearch]);

  // Global keyboard shortcut (Cmd/Ctrl + K)
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Cmd/Ctrl + K to toggle search
      if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        e.stopPropagation();
        setOpen(prev => !prev);
        return;
      }

      // Escape to close when search is open
      if (e.key === 'Escape' && open) {
        e.preventDefault();
        e.stopPropagation();
        setOpen(false);
        return;
      }
    };

    // Add listener to document to capture all key events
    document.addEventListener('keydown', handleKeyDown, true);
    return () => document.removeEventListener('keydown', handleKeyDown, true);
  }, [open]);

  // Reset query when opening/closing
  useEffect(() => {
    if (!open) {
      setQuery('');
      setResults([]);
    }
  }, [open]);

  return (
    <SearchContext.Provider value={{
      open,
      query,
      results,
      isSearching,
      globalResults,
      setOpen,
      setQuery
    }}>
      {children}
    </SearchContext.Provider>
  );
}

export function useSearch() {
  const context = useContext(SearchContext);
  if (!context) {
    throw new Error('useSearch must be used within a SearchProvider');
  }
  return context;
}
